import React, { useEffect, useState } from 'react'
import { isNil } from 'lodash'
import { Spin } from 'antd'
import {
  DownOutlined,
  FileTextOutlined,
  RightOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { useBillingProfileActivityLogQuery } from '../../api/client-billing-profile-api-client'
import { BillingProfileRecord } from '../../api/dto/client-billing-profile.dto'
import { ActivityLogEntry } from '../../api/dto/activity-log.dto'
import {
  ActivityEmpty,
  ActivityEntriesContainer,
  ActivityLine,
  ActivityMeta,
  ActivityPanel,
  ActivityPanelToggle,
  ActivityPanelToggleIcon,
} from './ClientBillingProfileManagement.styles'

/** One page deep enough to cover a profile's life, since there is no paging. */
const HISTORY_LIMIT = 50

const ACTION_LABELS: Record<string, string> = {
  BILLING_PROFILE_CREATE: 'Created',
  BILLING_PROFILE_UPDATE: 'Updated',
  BILLING_PROFILE_STATUS_CHANGE: 'Status changed',
}

const formatActivityEntry = (entry: ActivityLogEntry): string =>
  entry.description || ACTION_LABELS[entry.action] || entry.action

interface ChangeHistoryPanelProps {
  record: BillingProfileRecord
  open: boolean
}

/**
 * What changed and when, as one line each. Starts collapsed, so the log is
 * only fetched once someone asks for it. Field-level diffs are deliberately
 * not shown: they need more width than this modal has.
 */
const ChangeHistoryPanel = ({ record, open }: ChangeHistoryPanelProps) => {
  const [isPanelOpen, setIsPanelOpen] = useState(false)

  useEffect(() => {
    if (open) setIsPanelOpen(false)
  }, [open, record.id])

  const hasCompany = !isNil(record.clientPublicKey)

  const { data, isLoading } = useBillingProfileActivityLogQuery(
    {
      page: 1,
      limit: HISTORY_LIMIT,
      clientIds: hasCompany ? [record.clientPublicKey as string] : [],
      targetIds: [String(record.id)],
    },
    {
      enabled: open && isPanelOpen && hasCompany,
      refetchOnMount: true,
    }
  )

  const entries = data?.data ?? []

  const handleToggle = () => setIsPanelOpen((curr) => !curr)

  const renderEntries = () => {
    if (!hasCompany) {
      return (
        <ActivityEmpty>
          <FileTextOutlined style={{ fontSize: 24, opacity: 0.3 }} />
          No company linked, so there is no history to show
        </ActivityEmpty>
      )
    }
    if (isLoading) {
      return (
        <ActivityEmpty>
          <Spin size="small" />
        </ActivityEmpty>
      )
    }
    if (entries.length === 0) {
      return (
        <ActivityEmpty>
          <FileTextOutlined style={{ fontSize: 24, opacity: 0.3 }} />
          No changes recorded yet
        </ActivityEmpty>
      )
    }
    return entries.map((entry) => (
      <ActivityLine key={entry.id}>
        <div>{formatActivityEntry(entry)}</div>
        <ActivityMeta>
          {entry.actorName}{' '}
          {dayjs(entry.timestamp).format('MMM D, YYYY h:mm A')}
        </ActivityMeta>
      </ActivityLine>
    ))
  }

  return (
    <ActivityPanel>
      <ActivityPanelToggle
        type="button"
        onClick={handleToggle}
        aria-expanded={isPanelOpen}
      >
        <FileTextOutlined />
        Change history
        <ActivityPanelToggleIcon>
          {isPanelOpen ? <DownOutlined /> : <RightOutlined />}
        </ActivityPanelToggleIcon>
      </ActivityPanelToggle>

      {isPanelOpen ? (
        <ActivityEntriesContainer>{renderEntries()}</ActivityEntriesContainer>
      ) : null}
    </ActivityPanel>
  )
}

export { ChangeHistoryPanel }
